import React, { useContext, useEffect, useMemo, useState } from "react";
import {
  Divider,
  Grid,
  Link,
  Box,
  Input,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  IconButton,
  Typography,
  Menu,
  MenuItem,
  Checkbox,
  FormControlLabel,
  Paper,
  InputAdornment,
  Tooltip,
  Button,
} from "@mui/material";
import SettingsIcon from "@mui/icons-material/Settings";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ContentCopy from "@mui/icons-material/ContentCopy";
import Delete from "@mui/icons-material/Delete";
import SearchIcon from "@mui/icons-material/Search";
import DOMPurify from "dompurify";
import { useMutation } from "@apollo/client";
import { useNavigate } from "react-router-dom";

import { UserContext } from "../../../../../platform/components/auth/auth-context";
import { CLONE_BOOKING_TYPE, REMOVE_BOOKING_TYPES } from "../../../../../platform/graphql/mutations/bookingType";
import { useSelectedBusiness } from "../../../../../platform/redux/store";
import { getValueFromKey } from "../../../../../platform/util/meetingTypes";
import { Truncate } from "../../../../../platform/util/truncate";
import ActionsContainer from "./bookingTypeActionContainer";
import { getShareAvailabilityOptions } from './bookingShareStatus';
import { rootClientPath } from "../../../../../platform/util/link";
import { getCategoryName, useIsMobile } from "../../../../../platform/util/utils";
import ConfirmDialog from "./confirmDialog";
import { usePermissions } from "../../../../../platform/hooks/usePermissions";
import GenericTooltip from "../../../../../platform/components/common/genericToolTip";

/**
 * Notes:
 * - activeCategory: number (1/2/3) for a specific category, or null => show ALL.
 * - We derive categorized and filtered lists from props.data with useMemo.
 * - We build stable panel IDs: `panel-<cat>-<idOrIndex>`, so toggling remains consistent across filters.
 * - "Expand all" acts only on currently visible panels (respecting category + search).
 * - Settings menu enablement is computed from the *selected* item's createdBy (not the first in the list).
 */

const tooltipData = {
  bookingType:
    "Booking types can represent services, events, or classes. For example, you can create a booking type like '20-minute meeting' under the event category, allowing your customers to easily book that time slot.",
};

const TOUR_ONLY_BOOKING_TYPE_KEYWORDS = ["tour", "visit", "walkthrough"];

function isTourOnlyConciergeBookingType(bookingType) {
  if (!bookingType?.createdByAgentId) return false;

  const title = String(bookingType?.title || "").toLowerCase();
  const link = String(bookingType?.link || "").toLowerCase();

  return TOUR_ONLY_BOOKING_TYPE_KEYWORDS.some(
    (keyword) => title.includes(keyword) || link.includes(keyword)
  );
}

const BookingType = (props) => {
  const userContext = useContext(UserContext);
  const providerUser = userContext?.providerUser;
  const isMobile = useIsMobile();
  const selectedBusiness = useSelectedBusiness();
  const { canEditBookingType } = usePermissions();
  const navigate = useNavigate();

  // Slugs and booking URLs (guarded for undefineds)
  const bizSlug = selectedBusiness?.businessBasic?.url ?? "";
  const staffSlug = providerUser?.url ?? "";
  const staffBookingTypeUrl = bizSlug && staffSlug ? `${rootClientPath}/${bizSlug}/${staffSlug}` : "";
  const businessBookingPage = bizSlug ? `${rootClientPath}/${bizSlug}` : "";

  // UI state
  const [expanded, setExpanded] = useState([]); // string[] panel IDs
  const [areAllExpanded, setAreAllExpanded] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const [showCancelModal, setShowCancelModal] = useState(false);

  // Item currently selected in the Settings menu (for clone/delete & permissions)
  const [currentVal, setCurrentVal] = useState(null);

  // Search and category selection (null = ALL)
  const [search, setSearch] = useState("");
  const [activeCategory, setActiveCategory] = useState(null);

  // Optimistic local “removed” tracker so UI updates immediately after delete
  const [removedIds, setRemovedIds] = useState(() => new Set());

  const [removeBookingType] = useMutation(REMOVE_BOOKING_TYPES);
  const [cloneBookingType] = useMutation(CLONE_BOOKING_TYPE);

  /**
   * Categorize incoming data by bookingTypeCategoryId (1,2,3).
   * Re-runs whenever props.data changes.
   */
  const categorized = useMemo(() => {
    const src = (Array.isArray(props.data) ? props.data : []).filter(
      (bookingType) => !isTourOnlyConciergeBookingType(bookingType)
    );
    return {
      1: src.filter((v) => v?.bookingTypeCategoryId === 1),
      2: src.filter((v) => v?.bookingTypeCategoryId === 2),
      3: src.filter((v) => v?.bookingTypeCategoryId === 3),
    };
  }, [props.data]);

  /**
   * Apply search term to each category. We search by title (case-insensitive).
   * Also filter out any locally “removed” ids.
   */
  const filteredByCategory = useMemo(() => {
    const term = (search || "").trim().toLowerCase();
    const filterList = (list) =>
      list
        .filter((x) => !removedIds.has(x?.id))
        .filter((x) => (term ? (x?.title || "").toLowerCase().includes(term) : true));

    return {
      1: filterList(categorized[1]),
      2: filterList(categorized[2]),
      3: filterList(categorized[3]),
    };
  }, [categorized, search, removedIds]);

  /**
   * Build the array of visible panels (flattened if ALL), attaching stable panelId.
   * panelId = panel-<category>-<idOrIndex>
   */
  const visiblePanels = useMemo(() => {
    const toPanelObjs = (arr, cat) =>
      (arr || []).map((item, idx) => ({
        item,
        cat,
        panelId: `panel-${cat}-${item?.id ?? idx}`,
      }));

    if (activeCategory === 1 || activeCategory === 2 || activeCategory === 3) {
      return toPanelObjs(filteredByCategory[activeCategory], activeCategory);
    }
    // ALL
    return [
      ...toPanelObjs(filteredByCategory[1], 1),
      ...toPanelObjs(filteredByCategory[2], 2),
      ...toPanelObjs(filteredByCategory[3], 3),
    ];
  }, [filteredByCategory, activeCategory]);

  // Whether there is any data at all across categories (after search & removals)
  const hasData = useMemo(
    () =>
      (filteredByCategory[1]?.length ?? 0) +
        (filteredByCategory[2]?.length ?? 0) +
        (filteredByCategory[3]?.length ?? 0) >
      0,
    [filteredByCategory]
  );

  // Keep the "Expand all" checkbox in sync with how many visible panels are expanded
  useEffect(() => {
    setAreAllExpanded(visiblePanels.length > 0 && expanded.length === visiblePanels.length);
  }, [expanded, visiblePanels]);

  // Reset expanded when switching category or search (to avoid mismatches)
  useEffect(() => {
    setExpanded([]);
    setAreAllExpanded(false);
  }, [activeCategory, search]);

  // ────────────────────────────────
  // Handlers
  // ────────────────────────────────

  const handleSearch = (e) => {
    setSearch(e.target.value || "");
  };

  const handleToggleAll = (event) => {
    const checked = Boolean(event?.target?.checked);
    setAreAllExpanded(checked);
    if (checked) {
      // Expand all currently visible
      setExpanded(visiblePanels.map((v) => v.panelId));
    } else {
      setExpanded([]);
    }
  };

  const handleToggleOne = (panelId, isExpanded) => {
    setExpanded((prev) => {
      if (isExpanded) return prev.includes(panelId) ? prev : [...prev, panelId];
      return prev.filter((id) => id !== panelId);
    });
  };

  const handleSettingsClick = (event, val) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
    setCurrentVal(val || null);
  };

  const handleMenuClose = () => setAnchorEl(null);

  const cloneBType = async (event, id) => {
    event?.stopPropagation?.();
    if (props.canCreateMoreBookingTypes === false) {
      props.onUpgradeBookingTypeLimit?.();
      handleMenuClose();
      return;
    }
    try {
      const res = await cloneBookingType({ variables: { bookingTypeId: parseInt(id, 10) } });
      // Ensure list is refreshed, then navigate to edit page for new clone
      await props.refetch?.();
      const newId = res?.data?.cloneBookingType?.id;
      if (newId) navigate(`/biz/update/${newId}`);
    } catch (err) {
      console.error(err);
    } finally {
      handleMenuClose();
    }
  };

  const removeBookingTypeFromTheList = async (bookingTypeId) => {
    try {
      await removeBookingType({ variables: { bookingTypeId: parseInt(bookingTypeId, 10) } });
      // Optimistically drop it from UI
      setRemovedIds((prev) => new Set(prev).add(bookingTypeId));
      // Slight delay then refetch to sync with server truth
      setTimeout(() => props.refetch?.(), 200);
    } catch (err) {
      console.error(err);
    }
  };

  const handleConfirmDelete = async () => {
    if (currentVal?.id) {
      await removeBookingTypeFromTheList(currentVal.id);
      setShowCancelModal(false);
      setCurrentVal(null);
    }
  };

  // Compute permissions for the currently selected item in settings
  const isDisabledForCurrent =
    currentVal && typeof currentVal.createdBy !== "undefined"
      ? isTourOnlyConciergeBookingType(currentVal) || !canEditBookingType(Number(currentVal.createdBy))
      : true;

  return (
    <Grid container spacing={2}>
      {/* Left rail: headings, link copy, category selector */}
      <Grid item xs={12} md={3}>
        <Typography variant="h5" sx={{ ml: 2 }}>
          Booking Types <GenericTooltip title={tooltipData.bookingType} />
        </Typography>

        <Box sx={{ pl: 2 }}>
          {/* Booking Links Accordion */}
          <Accordion>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography variant="subtitle1">Booking Links</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Box sx={{ pl: 1, display: "flex", flexDirection: "column", gap: 0.5 }}>
                <Typography variant="caption" sx={{ fontWeight: 500 }}>
                  Business:
                </Typography>
                <Tooltip title={businessBookingPage || "Not available"} arrow placement="top-start">
                  <Box>
                    <ActionsContainer
                      url={businessBookingPage}
                      label="Copy Business Booking Link"
                      showLink
                      showLiveButton={false}
                      sx={{ wordBreak: "break-all", overflowWrap: "anywhere" }}
                    />
                  </Box>
                </Tooltip>

                <Typography variant="caption" sx={{ fontWeight: 500 }}>
                  Personal:
                </Typography>
                <Tooltip title={staffBookingTypeUrl || "Not available"} arrow placement="top-start">
                  <Box>
                    <ActionsContainer
                      url={staffBookingTypeUrl}
                      label="Copy Personal Booking Link"
                      showLink
                      showLiveButton={false}
                      sx={{ wordBreak: "break-all", overflowWrap: "anywhere" }}
                    />
                  </Box>
                </Tooltip>
              </Box>
            </AccordionDetails>
          </Accordion>

          <Divider sx={{ mt: 2 }} />
        </Box>

        {/* Category buttons */}
        <Box sx={{ display: "flex", flexDirection: "column", p: 2 }}>
          {/* ALL button */}
          <Button
            variant={activeCategory == null ? "contained" : "outlined"}
            onClick={() => setActiveCategory(null)}
            sx={{ mb: 1 }}
          >
            All (
            {(filteredByCategory[1]?.length || 0) +
              (filteredByCategory[2]?.length || 0) +
              (filteredByCategory[3]?.length || 0)}
            )
          </Button>

          {[1, 2, 3].map((category) => (
            <Button
              key={category}
              variant={activeCategory === category ? "contained" : "outlined"}
              onClick={() => setActiveCategory(category)}
              sx={{ mb: 1 }}
            >
              {getCategoryName(category)} ({filteredByCategory[category]?.length || 0})
            </Button>
          ))}
        </Box>
      </Grid>

      {/* Right: search, expand all, cards */}
      <Grid
        item
        xs={12}
        md={9}
        sx={{
          ml: isMobile ? 2 : 0,
          mr: isMobile ? 2 : 0,
          mt: { xs: 0.5, md: 6 },
        }}
      >
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: { xs: "stretch", sm: "center" },
            flexDirection: { xs: "column", sm: "row" },
            gap: 1.5,
            mb: 2,
          }}
        >
          <Paper
            sx={{
              display: "flex",
              alignItems: "center",
              width: { xs: "100%", sm: 320, md: 380 },
              maxWidth: "100%",
              px: 2,
              py: 1,
              borderRadius: "10px",
              alignSelf: { xs: "stretch", sm: "flex-start" },
            }}
          >
            <Input
              disableUnderline
              value={search}
              onChange={handleSearch}
              placeholder="Filter booking types"
              startAdornment={
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              }
              sx={{ flex: 1 }}
            />
          </Paper>

          {hasData && (
            <Box sx={{ display: "flex", justifyContent: { xs: "flex-start", sm: "flex-end" } }}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={areAllExpanded}
                    onChange={handleToggleAll}
                    inputProps={{ "aria-label": "Toggle expand all" }}
                  />
                }
                label={<Typography variant="body2">Expand all</Typography>}
                sx={{ mr: 0, ml: 0 }}
              />
            </Box>
          )}
        </Box>

        {/* Cards */}
        <Grid container spacing={2} sx={{ mt: -3, height: "auto", p: 0 }}>
          {visiblePanels.map(({ item: bookingType, panelId }) => {
            const onChange = (_, isExp) => handleToggleOne(panelId, isExp);
            const bookingUrl =
              bizSlug && bookingType?.link
                ? `${rootClientPath}/${bizSlug}/${bookingType.link}`
                : "";
            const canShareBookingType = Boolean(bookingType?.isPublic && bookingUrl);

            return (
              <Grid item xs={12} sm={6} md={4} key={panelId}>
                <Accordion
                  expanded={expanded.includes(panelId)}
                  onChange={onChange}
                  sx={{
                    borderRadius: "10px",
                    bgcolor: "background.paper",
                    backgroundImage: "none",
                    color: "text.primary",
                    boxShadow: "0 4px 6px rgba(0, 0, 0, 0.1)",
                    overflow: "hidden",
                  }}
                >
                  <AccordionSummary
                    expandIcon={<ExpandMoreIcon />}
                    sx={{ p: 1, minHeight: 30, alignItems: "center" }}
                  >
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, height: "100%" }}>
                      <Box sx={{ width: 4, height: 20, bgcolor: bookingType?.color || "divider" }} />
                      <Typography>{Truncate(bookingType?.title || "", 55)}</Typography>
                    </Box>
                  </AccordionSummary>

                  <AccordionDetails
                    sx={{
                      p: 2,
                      bgcolor: "background.paper",
                      color: "text.primary",
                      borderBottom: "1px solid",
                      borderColor: "divider",
                      position: "relative",
                    }}
                  >
                    {/* Settings icon (uses absolute so keep relative parent) */}
                    <Box sx={{ position: "absolute", top: 8, right: 8 }}>
                      <IconButton
                        onClick={(e) => handleSettingsClick(e, bookingType)}
                        aria-label={`settings-button-${bookingType?.id}`}
                        aria-controls="settings-menu"
                        aria-haspopup="true"
                        size="small"
                      >
                        <SettingsIcon fontSize="small" sx={{ color: "action.disabled" }} />
                      </IconButton>
                    </Box>

                    <Typography variant="body2" sx={{ color: "text.secondary", mb: 1 }}>
                      {getValueFromKey(bookingType?.meetingLocationTypeId)}
                    </Typography>

                    <Typography variant="body2" sx={{ color: "text.primary", mb: 2 }}>
                      <span
                        // Sanitize & truncate description
                        dangerouslySetInnerHTML={{
                          __html: DOMPurify.sanitize(Truncate(bookingType?.desc || "", 100)),
                        }}
                      />
                    </Typography>

                    <Link
                      underline="hover"
                      onClick={(e) => {
                        e.stopPropagation();
                        navigate(`/biz/update/${bookingType?.id}`);
                      }}
                      sx={{ cursor: "pointer", color: "primary.main", display: "block", mb: 2 }}
                    >
                      See Details
                    </Link>

                    <Divider sx={{ mb: 1 }} />

                    {canShareBookingType && (
                      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                        <ActionsContainer url={bookingUrl} bookingType={bookingType} availabilityOptions={getShareAvailabilityOptions(bookingType, selectedBusiness?.businessStaffs)} onEditSchedule={() => navigate(`/biz/update/${bookingType?.id}`)} />
                      </Box>
                    )}
                  </AccordionDetails>
                </Accordion>
              </Grid>
            );
          })}

          {/* Settings Menu */}
          <Menu id="settings-menu" anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleMenuClose}>
            <MenuItem
              disabled={isDisabledForCurrent}
              onClick={(e) => currentVal?.id && cloneBType(e, parseInt(currentVal.id, 10))}
            >
              <ContentCopy sx={{ color: "gray", mr: 1 }} /> Clone
            </MenuItem>
            <MenuItem
              disabled={isDisabledForCurrent}
              onClick={(e) => {
                e.stopPropagation();
                setShowCancelModal(true);
                handleMenuClose();
              }}
            >
              <Delete sx={{ color: "gray", mr: 1 }} /> Delete
            </MenuItem>
          </Menu>
        </Grid>

        {/* Deletion Confirmation Modal */}
        {showCancelModal && (
          <ConfirmDialog
            open={showCancelModal}
            onClose={() => setShowCancelModal(false)}
            onCancel={() => setShowCancelModal(false)}
            onConfirm={handleConfirmDelete}
            title="Confirm Deletion"
            bookingTitle={currentVal?.title || ""}
          />
        )}
      </Grid>
    </Grid>
  );
};

export default BookingType;
